'use client';

import { cn, Link, Popover, PopoverContent, PopoverTrigger, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { DISABLED_TOOLTIP } from '../access/resolve-access';
import { useHoverIntent } from '../hooks/use-hover-intent';
import type { ResolvedNavSection } from '../hooks/use-nav-sections';

interface NavSectionGroupProps {
    section: ResolvedNavSection;
    onOpenSynthesize: () => void;
}

/**
 * One expanded-nav section: a colored icon chip + bold label header (static
 * — not a toggle, every section is always fully expanded per the reference
 * screenshots), followed by its indented child rows.
 */
export function NavSectionGroup({ section, onOpenSynthesize }: NavSectionGroupProps) {
    return (
        <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2 px-2 py-0.5">
                <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg"
                    style={{
                        background: section.chipBg ?? `${section.color}22`,
                        color: section.iconColor ?? section.color,
                    }}
                >
                    <Icon icon={`lucide:${section.icon}`} width={13} height={13} />
                </span>
                <span className="text-[12.5px] font-bold leading-tight text-foreground">
                    {section.label}
                </span>
            </div>
            <div className="flex flex-col gap-0 pl-3">
                {section.children.map((child) => (
                    <NavChildRow
                        key={child.name}
                        child={child}
                        onOpenSynthesize={onOpenSynthesize}
                    />
                ))}
            </div>
        </div>
    );
}

NavSectionGroup.displayName = 'NavSectionGroup';

function NavChildRow({
    child,
    onOpenSynthesize,
}: {
    child: ResolvedNavSection['children'][number];
    onOpenSynthesize: () => void;
}) {
    // Controls the tooltip directly so hovering/focusing anywhere on the
    // row triggers it, not just the icon+text content — HeroUI's own
    // hover/focus detection only fires for its immediate trigger child,
    // which stays content-sized (see the comment below) to keep the
    // tooltip anchored close to the icon rather than off the row's full
    // stretched width. Closes on a short delay (see useHoverIntent) rather
    // than instantly, so the tooltip/flyout doesn't flicker open-closed-open
    // as the cursor crosses into it.
    const { isOpen: isHovered, setOpen: setIsHovered, handlers: hoverHandlers } = useHoverIntent();

    const hasFlyout = !!child.flyout?.length;

    const inert =
        !!child.comingSoon ||
        child.isDisabled ||
        (!child.href && !child.opensSynthesizeModal && !hasFlyout);

    const content = (
        <>
            <Icon
                icon={`lucide:${child.icon}`}
                width={15}
                height={15}
                className={inert ? 'text-default-300' : 'text-default-500'}
            />
            <span
                className={cn('text-[12.5px] font-medium', {
                    'font-semibold text-foreground': child.isSelected,
                    'text-default-600': !child.isSelected && !inert,
                    'text-default-300': inert,
                })}
            >
                {child.name}
            </span>
        </>
    );

    const rowClasses = cn('flex items-center gap-2 rounded-large px-2 py-0.5 hover:!opacity-100', {
        'bg-[#F3F8FF]': child.isSelected,
        'hover:bg-[#F3F8FF]': !inert && !child.isSelected,
        'cursor-pointer': inert,
    });

    if (inert) {
        return (
            <div className={rowClasses} aria-disabled {...hoverHandlers}>
                <Tooltip
                    content={
                        child.tooltip ?? DISABLED_TOOLTIP[child.disabledReason ?? 'coming-soon']
                    }
                    placement="right"
                    size="sm"
                    isOpen={isHovered}
                    classNames={{ content: 'bg-[#192138] px-3 py-1 text-[11px] text-white' }}
                >
                    <div className="flex items-center gap-2">{content}</div>
                </Tooltip>
            </div>
        );
    }

    // Tooltip wraps only the content-sized inner span, not the full-width
    // row itself — matching the inert branch above — so it sits close to
    // the icon/label instead of anchoring off the row's full stretched
    // width. Visibility is controlled via `isHovered`, set by the outer
    // row's own hover/focus handlers below, so the tooltip still appears
    // from anywhere on the row, not just over that inner content.
    const rowContent = child.tooltip ? (
        <Tooltip
            content={child.tooltip}
            placement="right"
            size="sm"
            isOpen={isHovered}
            classNames={{ content: 'bg-[#192138] px-3 py-1 text-[11px] text-white' }}
        >
            <div className="flex items-center gap-2">{content}</div>
        </Tooltip>
    ) : (
        content
    );

    if (child.opensSynthesizeModal) {
        return (
            <button
                type="button"
                onClick={onOpenSynthesize}
                className={rowClasses}
                {...(child.tooltip ? hoverHandlers : {})}
            >
                {rowContent}
            </button>
        );
    }

    // No `href` of its own — hovering reveals a small flyout of the links
    // it fans out to (e.g. Dashboards' Sigma dashboards), each its own
    // `Link`, rather than the row navigating anywhere directly. A plain
    // absolutely-positioned panel gets clipped by the sidebar's own
    // `overflow-y-auto` scroll container (which forces `overflow-x: auto`
    // too per the CSS overflow-computed-value rule), so this needs an
    // actual portaling overlay instead.
    if (hasFlyout) {
        return (
            <div className="relative">
                {/* Bridges the dead strip just past this row's right edge —
                    without it, hovering there (still visibly inside the
                    sidebar, past the chevron) reads as "left the row" and
                    dismisses the flyout the user is reaching for. `-mr-1`/`w-1`
                    match the scroller's own right padding (nav-shell's
                    `px-1`), the only space actually reachable here: this row
                    lives inside that `overflow-y-auto` ScrollShadow, so a
                    wider strip would just be clipped (and used to add
                    horizontal scrollable overflow before nav-shell's
                    `overflow-x-hidden`). Mirrors the collapsed rail's own
                    bridge in nav-hover-peek.tsx (`-mr-2 w-2` for its `p-2`).
                    Fully bridging the panel's outer `px-2` would need handlers
                    on an element outside the scroller. */}
                <div
                    className="absolute inset-y-0 right-0 -mr-1 w-1"
                    onMouseEnter={hoverHandlers.onMouseEnter}
                    onMouseLeave={hoverHandlers.onMouseLeave}
                />
                <Popover
                    placement="right-start"
                    isOpen={isHovered}
                    onOpenChange={setIsHovered}
                    classNames={{
                        content: 'w-56 p-1.5',
                        // Same fix as the collapsed rail's hover-peek
                        // (nav-hover-peek.tsx): HeroUI's default
                        // PopoverTrigger press affordance
                        // (aria-expanded:scale-[0.97] opacity-70) shrinks
                        // this row the instant it opens, pulling its own
                        // edge in from under a cursor resting right at it —
                        // a self-sustaining open/close blink. Neutralized
                        // here, not globally, since the affordance is still
                        // correct for an actually click-triggered popover.
                        trigger: 'aria-expanded:scale-100 aria-expanded:opacity-100',
                    }}
                >
                    <PopoverTrigger>
                        <div
                            className={cn(rowClasses, 'cursor-pointer justify-between', {
                                // CSS `:hover` alone drops the highlight the
                                // instant the mouse crosses into the flyout
                                // (a separate, portaled element) — driving it
                                // off `isHovered` instead keeps the row visibly
                                // tied to its own open flyout.
                                'bg-[#F3F8FF]': isHovered,
                            })}
                            // Mouse only: the Popover moves focus into its content when it
                            // opens, so focus/blur handlers here closed it and reopened it
                            // in a loop (BT-2078). Keyboard opens it through onOpenChange.
                            onMouseEnter={hoverHandlers.onMouseEnter}
                            onMouseLeave={hoverHandlers.onMouseLeave}
                        >
                            <span className="flex items-center gap-2">{content}</span>
                            <Icon
                                icon="lucide:chevron-right"
                                width={12}
                                height={12}
                                className="shrink-0 text-default-300"
                            />
                        </div>
                    </PopoverTrigger>
                    <PopoverContent
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        <div className="flex w-full flex-col gap-0.5">
                            {(child.flyout ?? []).map((item) =>
                                item.comingSoon ? (
                                    <Tooltip
                                        key={item.name}
                                        content={DISABLED_TOOLTIP['coming-soon']}
                                        placement="right"
                                        size="sm"
                                        classNames={{
                                            content:
                                                'bg-[#192138] px-3 py-1 text-[11px] text-white',
                                        }}
                                    >
                                        <div
                                            aria-disabled
                                            className="flex cursor-default items-center gap-2 rounded-large px-2 py-1.5 text-[12px] font-medium text-default-300"
                                        >
                                            <Icon
                                                icon={`lucide:${item.icon}`}
                                                width={14}
                                                height={14}
                                                className="shrink-0 text-default-300"
                                            />
                                            <span className="flex-1 truncate">{item.name}</span>
                                        </div>
                                    </Tooltip>
                                ) : (
                                    <Link
                                        key={item.name}
                                        href={item.href}
                                        className="flex items-center gap-2 rounded-large px-2 py-1.5 text-[12px] font-medium text-default-700 transition-colors hover:bg-[#F3F8FF]"
                                    >
                                        <Icon
                                            icon={`lucide:${item.icon}`}
                                            width={14}
                                            height={14}
                                            className="shrink-0 text-default-500"
                                        />
                                        <span className="flex-1 truncate">{item.name}</span>
                                    </Link>
                                ),
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        );
    }

    return (
        <Link href={child.href} className={rowClasses} {...(child.tooltip ? hoverHandlers : {})}>
            {rowContent}
        </Link>
    );
}
